import {
  MarketingPriceDropTriggerStep,
  PriceDropAmountType,
  TriggerStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import {
  CheckboxValue,
  RedoCheckbox,
} from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo } from "react";
import { PartialWithRequiredType } from "../../steps";

export function priceDropTriggerStepIsValid(
  value: PartialWithRequiredType<MarketingPriceDropTriggerStep>,
): boolean {
  if (!value.triggerSpecificFields?.minimumPriceDropAmount?.type) {
    return false;
  }

  if (
    !value.triggerSpecificFields?.minimumPriceDropAmount?.value ||
    value.triggerSpecificFields.minimumPriceDropAmount.value <= 0
  ) {
    return false;
  }

  if (!value.triggerSpecificFields?.contactRequirements) {
    return false;
  }

  const { productViewed, addedToCart, startedCheckout } =
    value.triggerSpecificFields.contactRequirements;
  if (
    !value.triggerSpecificFields?.daysBack ||
    value.triggerSpecificFields.daysBack < 1
  ) {
    return false;
  }
  return Boolean(productViewed || addedToCart || startedCheckout);
}

const priceDropAmountTypeOptions: RedoListItem<PriceDropAmountType>[] = [
  {
    value: PriceDropAmountType.PERCENTAGE,
    text: "%",
    type: "text",
    id: "percentage",
  },
  {
    value: PriceDropAmountType.CURRENCY,
    text: "$",
    type: "text",
    id: "currency",
  },
];

export const PriceDropFieldSelector = memo(function PriceDropFieldSelector({
  value: valueProp,
  valueChange,
}: {
  value: PartialWithRequiredType<MarketingPriceDropTriggerStep>;
  valueChange: (value: PartialWithRequiredType<TriggerStep>) => void;
}) {
  const { t } = useTranslation();
  const value =
    valueProp as PartialWithRequiredType<MarketingPriceDropTriggerStep>;

  const setTriggerSpecificFields = useCallback(
    (
      triggerSpecificFields: PartialWithRequiredType<MarketingPriceDropTriggerStep>["triggerSpecificFields"],
    ) => {
      valueChange({
        ...value,
        triggerSpecificFields: {
          minimumPriceDropAmount: {
            type: PriceDropAmountType.PERCENTAGE,
            value: 10,
            ...value.triggerSpecificFields?.minimumPriceDropAmount,
            ...triggerSpecificFields?.minimumPriceDropAmount,
          },
          contactRequirements: {
            productViewed: false,
            addedToCart: false,
            startedCheckout: false,
            ...value.triggerSpecificFields?.contactRequirements,
            ...triggerSpecificFields?.contactRequirements,
          },
          daysBack: 30,
          ...value.triggerSpecificFields,
          ...triggerSpecificFields,
        },
      });
    },
    [value, valueChange],
  );

  const amountTypeSelector = useMemo(
    () => (
      <RedoSingleSelectDropdownInput<PriceDropAmountType>
        fitToAnchor={false}
        options={priceDropAmountTypeOptions}
        optionSelected={({ item }) => {
          setTriggerSpecificFields({
            minimumPriceDropAmount: {
              type: item.value,
              value:
                value.triggerSpecificFields?.minimumPriceDropAmount?.value ??
                10,
            },
          });
        }}
        selectedItem={priceDropAmountTypeOptions.find(
          (option) =>
            option.value ===
            (value.triggerSpecificFields?.minimumPriceDropAmount?.type ??
              "percentage"),
        )}
        size="xs"
      />
    ),
    [value, setTriggerSpecificFields],
  );

  const handleContactRequirementChange = useCallback(
    (
      field: keyof MarketingPriceDropTriggerStep["triggerSpecificFields"]["contactRequirements"],
    ) =>
      (checked: CheckboxValue) => {
        const booleanValue = checked === true;
        setTriggerSpecificFields({
          contactRequirements: {
            ...value.triggerSpecificFields?.contactRequirements,
            [field]: booleanValue,
          },
        });
      },
    [value, setTriggerSpecificFields],
  );

  return (
    <Flex dir="column" gap="xl">
      {/* Price Drop Amount Configuration */}
      <Flex dir="column" gap="sm">
        <TriggerText>{t("Minimum price drop amount:")}</TriggerText>
        <Flex align="center" gap="sm">
          <RedoIncrementDecrement
            fullWidth
            max={
              value.triggerSpecificFields?.minimumPriceDropAmount?.type ===
              "percentage"
                ? 100
                : undefined
            }
            min={
              value.triggerSpecificFields?.minimumPriceDropAmount?.type ===
              "percentage"
                ? 1
                : 0.01
            }
            setValue={(numberValue) => {
              setTriggerSpecificFields({
                minimumPriceDropAmount: {
                  type:
                    value.triggerSpecificFields?.minimumPriceDropAmount?.type ??
                    PriceDropAmountType.PERCENTAGE,
                  value: numberValue,
                },
              });
            }}
            size="small"
            step={
              value.triggerSpecificFields?.minimumPriceDropAmount?.type ===
              "percentage"
                ? 1
                : 0.01
            }
            value={
              value.triggerSpecificFields?.minimumPriceDropAmount?.value ?? 10
            }
          />
          {amountTypeSelector}
        </Flex>
      </Flex>

      {/* Contact Requirements */}
      <Flex dir="column">
        <TriggerText>
          {t("Contact requirements (at least one must be selected):")}
        </TriggerText>
        <Flex dir="column" gap="sm">
          <RedoCheckbox
            label={t("Product viewed")}
            setValue={handleContactRequirementChange("productViewed")}
            size="sm"
            value={
              value.triggerSpecificFields?.contactRequirements?.productViewed ??
              false
            }
          />
          <RedoCheckbox
            label={t("Added to cart")}
            setValue={handleContactRequirementChange("addedToCart")}
            size="sm"
            value={
              value.triggerSpecificFields?.contactRequirements?.addedToCart ??
              false
            }
          />
          <RedoCheckbox
            label={t("Started checkout")}
            setValue={handleContactRequirementChange("startedCheckout")}
            size="sm"
            value={
              value.triggerSpecificFields?.contactRequirements
                ?.startedCheckout ?? false
            }
          />
        </Flex>
      </Flex>

      {/* Days Back Configuration */}
      <Flex dir="column" gap="sm">
        <TriggerText>{t("Time period:")}</TriggerText>
        <Flex align="center" gap="sm">
          <Text fontSize="xs" textColor="secondary">
            {t("Last")}
          </Text>
          <RedoIncrementDecrement
            max={365}
            min={1}
            setValue={(numberValue) => {
              setTriggerSpecificFields({ daysBack: numberValue });
            }}
            size="small"
            step={1}
            value={value.triggerSpecificFields?.daysBack ?? 30}
          />
          <Text fontSize="xs" textColor="secondary">
            {t("days")}
          </Text>
        </Flex>
        <Text fontSize="xs" textColor="tertiary">
          {t("How far back to look for customer activity")}
        </Text>
      </Flex>
    </Flex>
  );
});

const TriggerText = memo(function TriggerText({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Text fontSize="xs" textColor="secondary">
      {children}
    </Text>
  );
});
